import {
  Button,
  Divider,
  makeStyles,
  Persona,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  tokens,
  Text,
} from '@fluentui/react-components'
import {
  ClipboardTask24Regular,
  PersonFeedback48Regular,
  Question48Filled,
} from '@fluentui/react-icons'
import { useMsal } from '@azure/msal-react'
import { useState } from 'react'
import { useShellAuth } from './auth/ShellAuthContext'
import { FeedbackPanel } from './feedback/FeedbackPanel'
import { HelpPanel } from './help/HelpPanel'
import type { HelpSection } from './shell.types'
import type { ShellTelemetry } from './telemetry/shellTelemetry'

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    height: '54px',
    flex: '0 0 auto',
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    flexShrink: 0,
    borderRadius: tokens.borderRadiusLarge,
    border: '1px solid rgba(255, 255, 255, 0.35)',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
  },
  logoIcon: {
    width: '17px',
    stroke: 'currentColor',
    strokeWidth: '0.5px',
    strokeLinejoin: 'round',
  },
  title: {
    color: tokens.colorNeutralForegroundOnBrand,
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'nowrap',
  },
  titleAccent: {
    color: '#ffff00',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginLeft: 'auto',
  },
  action: {
    color: tokens.colorNeutralForegroundOnBrand,
    minWidth: '40px',
    '&:hover': {
      color: tokens.colorNeutralForegroundOnBrand,
    },
  },
  popover: {
    padding: tokens.spacingVerticalL,
    width: '180px',
  },
  divider: {
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalS,
  },
  popoverLink: {
    justifyContent: 'flex-start',
    width: '100%',
  },
})

interface HeaderProps {
  helpSections: HelpSection[]
  telemetry: ShellTelemetry
}

export const Header = ({ helpSections, telemetry }: HeaderProps) => {
  const styles = useStyles()
  const { user } = useShellAuth()
  const { instance } = useMsal()
  const userName = user.name ?? user.username
  const [feedbackLaunchRequestId, setFeedbackLaunchRequestId] = useState(0)
  const [isHelpOpen, setIsHelpOpen] = useState(false)

  return (
    <header className={styles.header}>
      <span className={styles.logo} aria-hidden="true">
        <ClipboardTask24Regular className={styles.logoIcon} fontSize={20} />
      </span>
      <Text size={400} className={styles.title}>
        Governance<span className={styles.titleAccent}>Hub</span>
      </Text>
      <nav className={styles.actions} aria-label="Header actions">
        <Button
          appearance="transparent"
          className={styles.action}
          icon={<PersonFeedback48Regular aria-hidden="true" />}
          aria-label="Send Feedback"
          title="Send Feedback"
          onClick={() => setFeedbackLaunchRequestId((current) => current + 1)}
        />
        <Button
          appearance="transparent"
          className={styles.action}
          icon={<Question48Filled aria-hidden="true" />}
          aria-label="Help"
          title="Help"
          onClick={() => {
            setIsHelpOpen(true)
            telemetry.event('help.open')
          }}
        />
        <Popover aria-label="User profile popover">
          <PopoverTrigger disableButtonEnhancement>
            <Persona
              size="large"
              aria-label="User profile"
              title="User profile"
              avatar={{
                name: userName,
                'aria-label': userName,
              }}
            />
          </PopoverTrigger>
          <PopoverSurface className={styles.popover}>
            <Persona
              name={userName}
              secondaryText="Available"
              presence={{ status: 'available' }}
              aria-label="Logged in user"
              size="extra-large"
              avatar={{
                name: userName,
                'aria-label': userName,
              }}
            />
            <Divider className={styles.divider} />
            <Button
              as="a"
              href="https://account.microsoft.com/profile"
              target="_blank"
              rel="noopener noreferrer"
              appearance="transparent"
              className={styles.popoverLink}
            >
              Your profile
            </Button>
            <Button
              as="a"
              href="https://account.microsoft.com"
              target="_blank"
              rel="noopener noreferrer"
              appearance="transparent"
              className={styles.popoverLink}
            >
              View account
            </Button>
            <Button
              appearance="transparent"
              className={styles.popoverLink}
              onClick={() => {
                const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0]
                instance.logoutRedirect({ account }).catch((error: unknown) => {
                  console.error('[shell] Sign out failed', error)
                })
              }}
            >
              Sign Out
            </Button>
          </PopoverSurface>
        </Popover>
      </nav>
      <FeedbackPanel launchRequestId={feedbackLaunchRequestId} />
      <HelpPanel
        open={isHelpOpen}
        sections={helpSections}
        telemetry={telemetry}
        onOpenChange={setIsHelpOpen}
      />
    </header>
  )
}
